import { amountBetween, duration, paceLabel, pageAt, secondsAt } from "./position.js";
import { tab as txt } from "./reading.js";
export const DAY = 86_400_000;
export const dateShort = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
/** "3 days ago", "yesterday", "today", "tomorrow", "in 5 days". */
export function countdown(iso) {
    const days = Math.ceil((new Date(iso).getTime() - Date.now()) / DAY);
    if (days < -1)
        return txt.daysAgo(-days);
    if (days === -1)
        return txt.yesterday;
    if (days === 0)
        return txt.today;
    if (days === 1)
        return txt.tomorrow;
    return txt.inDays(days);
}
/** What a meeting covers when the plan is custom: " · covers the whole book", " · through Part Two". */
export function coversText(plan) {
    if (!plan || !plan.custom)
        return "";
    if (plan.to === null)
        return txt.coversTheWholeBook;
    const lm = plan.landmarks.find((l) => l.page === plan.to);
    return txt.through(lm ? lm.name : txt.p(plan.to));
}
export function planLine(plan, myPercent, status, ctx) {
    if (plan.ended) {
        const met = plan.boundaries.length === 0 && plan.to !== null ? null : plan.boundaries.at(-1)?.metAt;
        return { kind: "ended", to: plan.to, met: met ? txt.met(dateShort(met)) : null };
    }
    if (status !== "reading")
        return null;
    const next = plan.landmarks.find((l) => !l.beyond && l.percent > myPercent);
    const targetPercent = next ? next.percent : plan.toPercent;
    const targetDate = next ? next.date : plan.deadline;
    const name = next ? next.name : plan.to !== null ? txt.throughP(plan.to) : txt.finish;
    if (!targetDate && !next)
        return null;
    const left = next && ctx.pages && (ctx.myFormat ?? "pages") === "pages"
        ? txt.pages2(Math.max(0, next.page - pageAt(myPercent, ctx.pages))) : amountBetween(myPercent, targetPercent, ctx);
    return { kind: "target", name, by: targetDate ? txt.by(dateShort(targetDate)) : "", there: myPercent >= targetPercent, left };
}
/** "p. 113 of 245 · 46%" split into the lead and the rest, in the reader's unit. */
export function whereParts(percent, ctx) {
    const f = ctx.myFormat ?? (ctx.pages ? "pages" : "percent");
    if (f === "pages" && ctx.pages)
        return { lead: txt.p2(pageAt(percent, ctx.pages)), rest: txt.of2(ctx.pages, percent) };
    if (f === "audio" && ctx.audioSeconds)
        return { lead: duration(secondsAt(percent, ctx.audioSeconds)), rest: txt.of3(duration(ctx.audioSeconds), percent) };
    return { lead: `${percent}%`, rest: ctx.pages ? txt.pOf2(pageAt(percent, ctx.pages), ctx.pages) : "" };
}
/** "5m ago", "3h ago", "2d ago". */
export function ago(iso) {
    if (!iso)
        return "";
    const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
    if (mins < 60)
        return txt.mAgo(Math.max(mins, 1));
    const hrs = Math.round(mins / 60);
    if (hrs < 48)
        return txt.hAgo(hrs);
    return txt.dAgo(Math.round(hrs / 24));
}
// The shared track (Reading, "Where everyone else is").
/** Where a face sits: read-before readers at the start, finishers at the end. */
export const trackPosition = (p) => (p.status === "read_before" ? 0 : p.status === "finished" ? 100 : p.percent);
/** A reader's status line: "finished Nov 3", "stopped at 40%", "read it before", their label, or how fresh the number is. */
export const trackWhen = (p) => p.status === "finished" ? txt.finished3(p.updatedAt ? ` ${dateShort(p.updatedAt)}` : "")
    : p.status === "dnf" ? txt.stoppedAt2(p.percent) : p.status === "read_before" ? txt.readItBefore2
        : p.label ?? (p.source === "hardcover" ? txt.viaHardcover(ago(p.updatedAt)) : p.updatedAt ? ago(p.updatedAt) : txt.notStarted);
export const trackPage = (p, pages) => pages && p.status === "reading" && p.percent > 0 ? txt.p2(pageAt(p.percent, pages)) : null;
/** The last landmark at or before a percent. */
export const trackLandmark = (plan, percent) => [...plan.landmarks].filter((l) => !l.beyond && l.percent <= percent).at(-1)?.name ?? null;
/** One reader, tapped: "p. 113 of 304 · 37% · past Part One · 3h ago", or their status line. */
export function trackDetail(p, pages, plan) {
    if (p.status !== "reading")
        return trackWhen(p);
    const pg = trackPage(p, pages), lm = trackLandmark(plan, p.percent);
    return [pg ? txt.of4(pg, pages) : null, `${p.percent}%`, lm ? txt.past(lm) : null,
        p.label ?? (p.source === "hardcover" ? txt.viaHardcover(ago(p.updatedAt)) : p.updatedAt ? ago(p.updatedAt) : txt.notStarted)].filter(Boolean).join(" · ");
}
/** A pile, tapped: "3 finished", "4 readers around p. 80 to p. 95", "4 readers around 26% to 31%". */
export const trackPileHead = (count, done, lo, hi, pages) => done ? txt.finished4(count) : pages ? txt.readersAroundPTo(count, pageAt(lo, pages), pageAt(hi, pages)) : txt.readersAroundTo(count, lo, hi);
/** The today mark's caption: "p. 120 today to finish by Nov 30". */
export const trackToday = (todayPercent, pages, deadline) => (pages ? txt.pToday(pageAt(todayPercent, pages)) : txt.hereToday) + (deadline ? txt.toFinishBy(dateShort(deadline)) : "");
/** The track's headcount: "just you so far", "2 finished", "3 readers". */
export const trackCount = (others, finished) => others === 0 ? txt.justYouSoFar : finished > 0 ? txt.finished2(finished) : `${others} ${others === 1 ? txt.reader : txt.readers}`;
/** The pace you'd need from here to make the next landmark (or the target) on time: "6 pages a day to stay on plan". */
export function paceToPlan(plan, percent, status, ctx) {
    if (status !== "reading" || plan.ended)
        return null;
    const next = plan.landmarks.find((l) => !l.beyond && l.percent > percent);
    const target = next ? next.percent : plan.toPercent;
    const dateIso = next ? next.date : plan.deadline;
    if (!dateIso)
        return null;
    const days = Math.max(1, Math.ceil((new Date(dateIso).getTime() - Date.now()) / DAY));
    if (plan.todayPercent !== null && percent >= plan.todayPercent)
        return txt.aheadOfPlan;
    if (percent < target)
        return txt.toStayOnPlan(paceLabel((target - percent) / days, ctx));
    return null;
}
/** The day count in the hero: day 1 is the day the book started. */
export const dayNumber = (startedAt) => startedAt ? Math.max(1, Math.floor((Date.now() - new Date(startedAt).getTime()) / DAY) + 1) : null;
//# sourceMappingURL=readingWords.js.map